import * as React from 'react';
import {
    Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, FormLabel, Grid, IconButton, MenuItem, Radio, RadioGroup, Stack, Switch, Tab, Table, TableBody, TableCell,
    TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
    AI_LABELS, createAiBudget, deleteAiBudget, getAiAgents, getAiBudgetEvents, getAiBudgets, getAiMeta, getAiTasks, getAiWorkflows,
    updateAiBudget,
} from '../../Api';
import {
    AI_BUDGET_ACTIONS, AI_BUDGET_PERIODS, AiBudgetAction, AiBudgetDto, AiBudgetEventDto, AiBudgetPeriod, AiMetaDto,
    AiUpsertBudgetRequest,
} from '../../models/dtos/AiDto';
import { ConfirmDialog } from '../../components/betupFeed/BetupFeedShared';
import { BudgetProgress } from '../../components/ai/AiHealthPanels';
import AiUserBudgetsTab from '../../components/ai/AiUserBudgets';
import {
    AiActionError, AiBudgetStateChip, AiErrorAlert, aiLoadErrorText, EnumSelect, formatAiDate, formatAiUsd,
    parseOptionalNumber, textError, toAiActionError,
} from '../../components/ai/AiShared';

type Scope = 'none' | 'task' | 'agent' | 'workflow';

const TABS = [
    { key: 'company', label: 'Company' },
    { key: 'per-user', label: 'Per user' },
];

interface BudgetForm {
    name: string;
    scope: Scope;
    taskCode: string | null;
    agentCode: string | null;
    workflowCode: string | null;
    caller: string;
    period: AiBudgetPeriod;
    limitUsd: string;
    alertPct: string;
    action: AiBudgetAction;
    enabled: boolean;
}

const EMPTY: BudgetForm = {
    name: '', scope: 'none', taskCode: null, agentCode: null, workflowCode: null, caller: '', period: 'MONTH', limitUsd: '', alertPct: '80', action: 'ALERT', enabled: true,
};

function BudgetDialog({ open, budget, meta, taskCodes, agentCodes, workflowCodes, onClose, onSaved }: {
    open: boolean;
    budget: AiBudgetDto | null;
    meta: AiMetaDto | null;
    taskCodes: string[];
    agentCodes: string[];
    workflowCodes: string[];
    onClose: () => void;
    onSaved: (message: string) => void;
}) {
    const [form, setForm] = React.useState<BudgetForm>(EMPTY);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        setError(null);
        setForm(budget ? {
            name: budget.name,
            scope: budget.workflowCode ? 'workflow' : budget.taskCode ? 'task' : budget.agentCode ? 'agent' : 'none',
            taskCode: budget.taskCode,
            agentCode: budget.agentCode,
            workflowCode: budget.workflowCode ?? null,
            caller: budget.caller ?? '',
            period: budget.period,
            limitUsd: String(budget.limitUsd),
            alertPct: String(budget.alertPct),
            action: budget.action,
            enabled: budget.enabled,
        } : EMPTY);
    }, [open, budget]);

    const patch = (p: Partial<BudgetForm>) => setForm(f => ({ ...f, ...p }));

    const save = async () => {
        if (!form.name.trim()) return setError(textError('Name is required.'));
        const limitUsd = parseOptionalNumber(form.limitUsd);
        if (limitUsd == null || limitUsd <= 0) return setError(textError('Limit must be a positive number.'));
        const alertPct = parseOptionalNumber(form.alertPct);
        if (alertPct == null || alertPct < 0 || alertPct > 100) return setError(textError('Alert % must be between 0 and 100.'));
        if (form.scope === 'task' && !form.taskCode) return setError(textError('Pick a task.'));
        if (form.scope === 'agent' && !form.agentCode) return setError(textError('Pick an agent.'));
        if (form.scope === 'workflow' && !form.workflowCode) return setError(textError('Pick a workflow.'));
        const request: AiUpsertBudgetRequest = {
            name: form.name.trim(),
            taskCode: form.scope === 'task' ? form.taskCode : null,
            agentCode: form.scope === 'agent' ? form.agentCode : null,
            workflowCode: form.scope === 'workflow' ? form.workflowCode : null,
            caller: form.caller || null,
            period: form.period,
            limitUsd,
            alertPct,
            action: form.action,
            enabled: form.enabled,
        };
        setSaving(true);
        const result = budget ? await updateAiBudget(budget.id, request) : await createAiBudget(request);
        setSaving(false);
        if (result.error != null) return setError(toAiActionError(result, 'Save failed'));
        onSaved(`Budget ${request.name} saved.`);
    };

    const callers = meta?.callers ?? [];
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{budget ? `Edit budget ${budget.name}` : 'New budget'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12}>
                        <TextField label="Name" fullWidth required value={form.name} onChange={e => patch({ name: e.target.value })} />
                    </Grid>
                    <Grid item xs={12}>
                        <FormLabel>Scope</FormLabel>
                        <RadioGroup row value={form.scope} onChange={e => patch({ scope: e.target.value as Scope })}>
                            <FormControlLabel value="none" control={<Radio />} label="All tasks & agents" />
                            <FormControlLabel value="task" control={<Radio />} label="One task" />
                            <FormControlLabel value="agent" control={<Radio />} label="One agent" />
                            <FormControlLabel value="workflow" control={<Radio />} label="One workflow" />
                        </RadioGroup>
                    </Grid>
                    {form.scope === 'workflow' && (
                        <Grid item xs={12}>
                            <Autocomplete options={workflowCodes} value={form.workflowCode}
                                onChange={(_, v) => patch({ workflowCode: v })}
                                renderInput={params => <TextField {...params} label="Workflow"
                                    helperText="Every call of its agents and tasks. With BLOCK, an exhausted budget stops new workflow instances (run keys); a started one finishes. Task and agent budgets still apply inside it." />} />
                        </Grid>
                    )}
                    {form.scope === 'task' && (
                        <Grid item xs={12}>
                            <Autocomplete freeSolo options={taskCodes} value={form.taskCode}
                                onChange={(_, v) => patch({ taskCode: v })} onInputChange={(_, v) => patch({ taskCode: v || null })}
                                renderInput={params => <TextField {...params} label="Task code" />} />
                        </Grid>
                    )}
                    {form.scope === 'agent' && (
                        <Grid item xs={12}>
                            <Autocomplete freeSolo options={agentCodes} value={form.agentCode}
                                onChange={(_, v) => patch({ agentCode: v })} onInputChange={(_, v) => patch({ agentCode: v || null })}
                                renderInput={params => <TextField {...params} label="Agent code" helperText="Counts the agent's whole run tree." />} />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                        <TextField select fullWidth label="Caller" value={form.caller} onChange={e => patch({ caller: e.target.value })}
                            helperText="Limit to one calling service (optional)">
                            <MenuItem value=""><em>any caller</em></MenuItem>
                            {callers.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            {form.caller && !callers.includes(form.caller) && <MenuItem value={form.caller}>{form.caller}</MenuItem>}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <EnumSelect label="Period" value={form.period} options={meta?.budgetPeriods ?? AI_BUDGET_PERIODS}
                            onChange={v => v && patch({ period: v })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Limit (USD)" fullWidth required value={form.limitUsd}
                            onChange={e => patch({ limitUsd: e.target.value.replace(/[^\d.]/g, '') })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Alert at %" fullWidth value={form.alertPct}
                            onChange={e => patch({ alertPct: e.target.value.replace(/[^\d.]/g, '') })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <EnumSelect label="Action" value={form.action} options={meta?.budgetActions ?? AI_BUDGET_ACTIONS}
                            onChange={v => v && patch({ action: v })} />
                    </Grid>
                    {form.action === 'BLOCK' && (
                        <Grid item xs={12}>
                            <Alert severity="warning">BLOCK rejects calls with 429 BUDGET_EXCEEDED once the limit is reached, until the period ends.</Alert>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <FormControlLabel control={<Switch checked={form.enabled} onChange={e => patch({ enabled: e.target.checked })} />} label="Enabled" />
                    </Grid>
                </Grid>
                <AiErrorAlert error={error} onClose={() => setError(null)} sx={{ mt: 2 }} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </DialogActions>
        </Dialog>
    );
}

function BudgetEventsGrid({ budgets, reloadKey }: { budgets: AiBudgetDto[]; reloadKey: number }) {
    const [budgetId, setBudgetId] = React.useState<number | ''>('');
    const [rows, setRows] = React.useState<AiBudgetEventDto[]>([]);
    const [rowCount, setRowCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: 25 });

    React.useEffect(() => { setPaginationModel(p => (p.page === 0 ? p : { ...p, page: 0 })); }, [budgetId]);

    const load = React.useCallback(async () => {
        setLoading(true);
        const page = await getAiBudgetEvents({ budgetId: budgetId === '' ? null : budgetId },
            paginationModel.page * paginationModel.pageSize, paginationModel.pageSize);
        setLoading(false);
        if (page == null) {
            setError(aiLoadErrorText(AI_LABELS.budgetEvents, 'Failed to load budget events.'));
            setRows([]);
            setRowCount(0);
            return;
        }
        setError(null);
        setRows(page.items ?? []);
        setRowCount(page.totalItems ?? 0);
    }, [budgetId, paginationModel]);
    React.useEffect(() => { load(); }, [load, reloadKey]);

    const columns: GridColDef<AiBudgetEventDto>[] = [
        { field: 'createdAt', headerName: 'At', width: 170, sortable: false, valueFormatter: ({ value }) => formatAiDate(value as string) },
        { field: 'budgetName', headerName: 'Budget', width: 180, sortable: false },
        {
            field: 'type', headerName: 'Type', width: 140, sortable: false,
            renderCell: ({ row }) => (
                <Chip size="small" label={row.type}
                    color={row.type === 'FIRST_BLOCK' || row.type === 'LIMIT_REACHED' ? 'error' : row.type === 'THRESHOLD' ? 'warning' : 'info'} />
            ),
        },
        { field: 'spentUsd', headerName: 'Spent', width: 110, sortable: false, valueFormatter: ({ value }) => formatAiUsd(value as number) },
        { field: 'limitUsd', headerName: 'Limit', width: 110, sortable: false, valueFormatter: ({ value }) => formatAiUsd(value as number) },
        { field: 'forecastUsd', headerName: 'Forecast', width: 110, sortable: false, valueFormatter: ({ value }) => formatAiUsd(value as number | null) },
        { field: 'periodStart', headerName: 'Period start', width: 170, sortable: false, valueFormatter: ({ value }) => formatAiDate(value as string) },
        {
            field: 'trigger', headerName: 'Triggered by', flex: 1, minWidth: 220, sortable: false,
            valueGetter: ({ row }) => [row.triggerCaller, row.triggerTaskCode && `task ${row.triggerTaskCode}`, row.triggerAgentCode && `agent ${row.triggerAgentCode}`]
                .filter(Boolean).join(' · ') || '—',
        },
    ];

    return (
        <Stack gap={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Events</Typography>
                <TextField select size="small" label="Budget" value={budgetId} sx={{ minWidth: 240 }}
                    onChange={e => setBudgetId(e.target.value === '' ? '' : Number(e.target.value))}>
                    <MenuItem value=""><em>All budgets</em></MenuItem>
                    {budgets.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
                </TextField>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnMenu
                density="compact"
                localeText={{ noRowsLabel: 'No budget events' }}
            />
        </Stack>
    );
}

export default function AiBudgetsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabKey = searchParams.get('tab') === 'per-user' ? 'per-user' : 'company';
    const tabIndex = Math.max(0, TABS.findIndex(t => t.key === tabKey));
    const [meta, setMeta] = React.useState<AiMetaDto | null>(null);
    const [budgets, setBudgets] = React.useState<AiBudgetDto[]>([]);
    const [taskCodes, setTaskCodes] = React.useState<string[]>([]);
    const [agentCodes, setAgentCodes] = React.useState<string[]>([]);
    const [workflowCodes, setWorkflowCodes] = React.useState<string[]>([]);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [dialog, setDialog] = React.useState<{ open: boolean; budget: AiBudgetDto | null }>({ open: false, budget: null });
    const [pendingDelete, setPendingDelete] = React.useState<AiBudgetDto | null>(null);
    const [reloadKey, setReloadKey] = React.useState(0);

    const load = React.useCallback(async () => {
        const b = await getAiBudgets();
        setBudgets(b ?? []);
        setLoadError(b == null ? aiLoadErrorText(AI_LABELS.budgets, 'Failed to load budgets.') : null);
        setReloadKey(k => k + 1);
    }, []);

    React.useEffect(() => {
        load();
        getAiMeta().then(setMeta);
        getAiTasks({}, 0, 200).then(p => setTaskCodes((p?.items ?? []).map(t => t.code)));
        getAiAgents({}, 0, 200).then(p => setAgentCodes((p?.items ?? []).map(a => a.code)));
        getAiWorkflows().then(list => setWorkflowCodes((list ?? []).map(w => w.code)));
    }, [load]);

    const remove = async () => {
        if (pendingDelete == null) return;
        const target = pendingDelete;
        setPendingDelete(null);
        const result = await deleteAiBudget(target.id);
        if (result.error != null) {
            setError(toAiActionError(result, 'Delete failed'));
            return;
        }
        setNotice(`Budget ${target.name} deleted.`);
        load();
    };

    return (
        <Stack gap={2} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4">AI budgets</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {tabKey === 'company'
                            ? 'Daily / monthly spend limits for everything, one task or one agent, optionally per caller. ALERT notifies; BLOCK rejects calls.'
                            : 'Rolling short-window and weekly spend limits per player (end user), optionally per caller, target and tier.'}
                    </Typography>
                </Box>
                {tabKey === 'company' && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, budget: null })}>New budget</Button>
                )}
            </Stack>
            <Tabs value={tabIndex} onChange={(_, i: number) => setSearchParams(i === 0 ? {} : { tab: TABS[i].key }, { replace: true })}
                sx={{ borderBottom: 1, borderColor: 'divider' }}>
                {TABS.map(t => <Tab key={t.key} label={t.label} />)}
            </Tabs>
            {tabKey === 'per-user' && <AiUserBudgetsTab meta={meta} taskCodes={taskCodes} agentCodes={agentCodes} />}
            {tabKey === 'company' && (
                <>
                    {loadError && <Alert severity="error">{loadError}</Alert>}
                    <AiErrorAlert error={error} onClose={() => setError(null)} />
                    {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
                    <Card variant="outlined">
                        <CardContent>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Budget</TableCell>
                                        <TableCell>Scope</TableCell>
                                        <TableCell>Period</TableCell>
                                        <TableCell>Spent / limit</TableCell>
                                        <TableCell sx={{ minWidth: 180 }}>Used</TableCell>
                                        <TableCell>Forecast</TableCell>
                                        <TableCell>Blocked</TableCell>
                                        <TableCell>State</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {budgets.length === 0 && <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary">No budgets.</Typography></TableCell></TableRow>}
                                    {budgets.map(b => (
                                        <TableRow key={b.id} sx={{ opacity: b.enabled ? 1 : 0.6 }}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>{b.name}</Typography>
                                                <Stack direction="row" gap={0.5} sx={{ mt: 0.5 }}>
                                                    <Chip size="small" variant="outlined" color={b.action === 'BLOCK' ? 'error' : 'default'} label={b.action} />
                                                    {!b.enabled && <Chip size="small" variant="outlined" label="disabled" />}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{[b.workflowCode ? `workflow ${b.workflowCode}` : null, b.taskCode ? `task ${b.taskCode}` : b.agentCode ? `agent ${b.agentCode}` : null].filter(Boolean).join(' · ') || 'all'}</Typography>
                                                <Typography variant="caption" color="text.secondary">{b.caller ? `caller ${b.caller}` : 'any caller'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{b.period}</Typography>
                                                <Typography variant="caption" color="text.secondary">from {formatAiDate(b.periodStart)}</Typography>
                                            </TableCell>
                                            <TableCell>{formatAiUsd(b.spentUsd)} / {formatAiUsd(b.limitUsd)}</TableCell>
                                            <TableCell>
                                                <BudgetProgress percent={b.percent} state={b.state} />
                                                <Typography variant="caption" color="text.secondary">alert at {b.alertPct}%</Typography>
                                            </TableCell>
                                            <TableCell>{formatAiUsd(b.forecastUsd)}</TableCell>
                                            <TableCell>{b.blockedInPeriod}</TableCell>
                                            <TableCell><AiBudgetStateChip state={b.state} /></TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                <Tooltip title="Edit"><IconButton size="small" onClick={() => setDialog({ open: true, budget: b })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setPendingDelete(b)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                    <BudgetEventsGrid budgets={budgets} reloadKey={reloadKey} />
                </>
            )}
            <BudgetDialog open={dialog.open} budget={dialog.budget} meta={meta} taskCodes={taskCodes} agentCodes={agentCodes} workflowCodes={workflowCodes}
                onClose={() => setDialog({ open: false, budget: null })}
                onSaved={message => { setDialog({ open: false, budget: null }); setNotice(message); setError(null); load(); }} />
            <ConfirmDialog
                open={pendingDelete != null}
                title="Delete budget?"
                text={<>Budget <b>{pendingDelete?.name}</b> and its limit will be removed{pendingDelete?.action === 'BLOCK' ? ' — blocked calls resume immediately' : ''}.</>}
                confirmLabel="Delete"
                confirmColor="error"
                onClose={() => setPendingDelete(null)}
                onConfirm={remove}
            />
        </Stack>
    );
}
